// @ts-nocheck
import React, { useContext, useEffect, useState } from "react";
import { AuthContext } from "../AuthProvider";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import NotificationPreferencesSection from "../features/notifications/NotificationPreferencesSection";
import Modal from "../components/Modal";
import config from "../config";
import http from "../api/apiClient";
import { mapHttpErrorToMessage } from "../utils/mapHttpErrorToMessage";
import { parseToDate } from "../utils/dateUtils";
import {
  availableStartPages,
  pickStartPagePreference,
  withStartPagePreference,
  START_PAGE_DASHBOARD,
  START_PAGE_MY_ENVIRONMENT,
  START_PAGE_REPORTS,
  START_PAGE_PORTAL,
} from "../utils/startPagePreference";
import { useStartPageAccess } from "../utils/useStartPageAccess";
import {
  FaBell,
  FaChevronRight,
  FaGlobe,
  FaPaperclip,
  FaShieldAlt,
  FaSyncAlt,
  FaTicketAlt,
  FaUserCircle,
} from "react-icons/fa";
import { LdsOverviewHero, LdsOverviewPage } from "../components/lds/LdsOverview";
import {
  LdsTable,
  LdsTableBody,
  LdsTableCard,
  LdsTableCell,
  LdsTableHead,
  LdsTableHeadCell,
  LdsTableRow,
  LdsTableScroll,
} from "../components/lds/LdsTable";

// Bepaalt taal uit user attributes (voorrang) of uit token-claims
function pickLocale(kc, profileObj) {
  const attr = profileObj?.attributes || {};
  const fromAttr =
    attr.locale?.[0] ||
    attr.locale ||
    attr.preferred_language?.[0] ||
    attr.preferred_language ||
    null;

  return (
    fromAttr ||
    kc?.idTokenParsed?.locale ||
    kc?.tokenParsed?.locale ||
    kc?.idTokenParsed?.preferred_language ||
    kc?.tokenParsed?.preferred_language ||
    null
  );
}

const AVAILABLE_LOCALES = [
  { value: "en", label: "English" },
  { value: "nl", label: "Nederlands" },
];

const startPageLabel = (t, page) => {
  switch (page) {
    case START_PAGE_MY_ENVIRONMENT:
      return t("profile.start_page.my_environment", { defaultValue: "My environment" });
    case START_PAGE_REPORTS:
      return t("profile.start_page.reports", { defaultValue: "Reports" });
    case START_PAGE_PORTAL:
      return t("profile.start_page.portal", { defaultValue: "Parent portal" });
    default:
      return t("profile.start_page.dashboard", { defaultValue: "Dashboard" });
  }
};

const StartPagePreferenceField = ({ t, value, options, onChange, className = "" }) => (
  <label className={className || "block text-sm font-semibold text-slate-600 dark:text-slate-300"}>
    {t("profile.start_page.label", { defaultValue: "Start page after sign-in" })}
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="mt-2 min-h-[44px] w-full rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-900 shadow-sm focus:border-indigo-400 focus:outline-none focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-950/40 dark:text-slate-100 dark:focus:ring-indigo-900/40"
    >
      {options.map((page) => (
        <option key={page} value={page}>
          {startPageLabel(t, page)}
        </option>
      ))}
    </select>
    <span className="mt-2 block text-xs font-normal text-slate-500 dark:text-slate-400">
      {t("profile.start_page.hint", {
        defaultValue: "Choose which page opens first when you sign in.",
      })}
    </span>
  </label>
);

export function ProfileBody({ variant = "staff" }) {
  const { keycloak } = useContext(AuthContext);
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [locale, setLocale] = useState("en");
  const [startPage, setStartPage] = useState(START_PAGE_DASHBOARD);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [myIssues, setMyIssues] = useState([]);
  const [myIssuesLoading, setMyIssuesLoading] = useState(false);
  const [myIssuesLoaded, setMyIssuesLoaded] = useState(false);
  const [myIssuesError, setMyIssuesError] = useState(null);
  const [myIssuesStatusFilter, setMyIssuesStatusFilter] = useState("open");
  const [activeIssue, setActiveIssue] = useState(null);
  const [issueDrawerOpen, setIssueDrawerOpen] = useState(false);
  const [issueDetails, setIssueDetails] = useState(null);
  const [issueDetailsLoading, setIssueDetailsLoading] = useState(false);
  const [issueDetailsError, setIssueDetailsError] = useState(null);
  const [issueComments, setIssueComments] = useState([]);
  const [issueCommentsLoading, setIssueCommentsLoading] = useState(false);
  const [issueCommentsError, setIssueCommentsError] = useState(null);
  const [issueAssets, setIssueAssets] = useState([]);
  const [issueAssetsLoading, setIssueAssetsLoading] = useState(false);
  const [issueAssetsError, setIssueAssetsError] = useState(null);
  const [issueAssetObjectUrls, setIssueAssetObjectUrls] = useState({});
  const [newIssueComment, setNewIssueComment] = useState("");
  const [newIssueCommentSaving, setNewIssueCommentSaving] = useState(false);
  const [newIssueCommentError, setNewIssueCommentError] = useState(null);
  const [issueDrawerShowDefaultRoles, setIssueDrawerShowDefaultRoles] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: "", next: "", confirm: "" });
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordSaved, setPasswordSaved] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [activeProfileSection, setActiveProfileSection] = useState("profile");
  const workflowEnrichedKeyRef = React.useRef("");
  const startPageOptions = availableStartPages(useStartPageAccess());
  const canChooseStartPage = startPageOptions.length > 1;
  const effectiveStartPage = startPageOptions.includes(startPage)
    ? startPage
    : startPageOptions[0] || START_PAGE_DASHBOARD;

  const username = keycloak?.idTokenParsed?.preferred_username || "unknown";
  const base = `${keycloak.authServerUrl.replace(/\/+$/, "")}/realms/${keycloak.realm}`;
  const accountApiUrl = `${base}/account`; // GET/POST profile
  const passwordApiUrl = `${config.apiBaseUrl}/profile/password`;
  const parentTopicsConfig = [
    {
      topic: "PARENT_ACTION",
      requiredRole: "",
      labelKey: "notifications.preferences.topics.PARENT_ACTION.label",
      descriptionKey: "notifications.preferences.topics.PARENT_ACTION.description",
      defaultLabel: "Parent action updates",
      defaultDescription: "Receive an email when there are new parent actions.",
    },
  ];

  const isParentDesktop = variant === "parentDesktop";
  const isParentVariant = variant === "parent" || isParentDesktop;

  const wrapperClasses =
    variant === "parent"
      ? "space-y-4 text-slate-950 dark:text-slate-100"
      : "space-y-6 text-gray-900 dark:text-gray-200";
  const cardClasses =
    variant === "parent"
      ? "rounded-[1.75rem] border border-slate-200/80 bg-white/95 p-5 shadow-sm backdrop-blur dark:border-slate-800 dark:bg-slate-950/70"
      : "rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-slate-900/80";
  const secondaryButtonClass =
    isParentVariant
      ? isParentDesktop
        ? "inline-flex min-h-[44px] items-center justify-center rounded-full border border-slate-200 bg-white px-5 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-emerald-100 dark:border-slate-700 dark:bg-slate-950/30 dark:text-slate-200 dark:focus:ring-emerald-900/30"
        : "inline-flex min-h-[42px] items-center justify-center rounded-full border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-emerald-100 dark:border-slate-700 dark:bg-slate-950/30 dark:text-slate-200 dark:focus:ring-emerald-900/30"
      : "inline-flex min-h-[44px] items-center justify-center rounded-full border border-indigo-100/70 bg-white px-5 text-sm font-bold text-indigo-700 shadow-sm transition hover:bg-indigo-50 focus:outline-none focus:ring-4 focus:ring-indigo-100 dark:border-indigo-500/40 dark:bg-slate-900/60 dark:text-indigo-200 dark:hover:bg-indigo-950/40";
  const primaryButtonClass =
    isParentVariant
      ? isParentDesktop
        ? "inline-flex min-h-[44px] items-center justify-center rounded-full bg-emerald-600 px-5 text-sm font-bold text-white shadow-sm transition hover:bg-emerald-500 focus:outline-none focus:ring-4 focus:ring-emerald-100 disabled:opacity-60 dark:focus:ring-emerald-900/30"
        : "inline-flex min-h-[42px] items-center justify-center rounded-full bg-emerald-600 px-4 text-sm font-bold text-white shadow-sm transition hover:bg-emerald-500 focus:outline-none focus:ring-4 focus:ring-emerald-100 disabled:opacity-60 dark:focus:ring-emerald-900/30"
      : "inline-flex min-h-[44px] items-center justify-center rounded-full bg-indigo-600 px-5 text-sm font-bold text-white shadow-sm transition hover:bg-indigo-700 focus:outline-none focus:ring-4 focus:ring-indigo-100 disabled:opacity-60 dark:focus:ring-indigo-900/40";

  const parseIssueCreatedAt = (value) => {
    if (value == null) return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

    if (typeof value === "number" && Number.isFinite(value)) {
      const ms = value < 1e12 ? value * 1000 : value;
      const d = new Date(ms);
      return Number.isNaN(d.getTime()) ? null : d;
    }

    if (typeof value === "string") {
      const trimmed = value.trim();
      if (/^\d+$/.test(trimmed)) {
        const asNumber = Number(trimmed);
        if (Number.isFinite(asNumber)) {
          const ms = asNumber < 1e12 ? asNumber * 1000 : asNumber;
          const d = new Date(ms);
          return Number.isNaN(d.getTime()) ? null : d;
        }
      }
    }

    return parseToDate(value);
  };

  const parseIssueBody = (raw) => {
    const text = raw == null ? "" : String(raw);
    if (!text.trim()) return { summary: "", meta: [], details: "" };
    const lines = text.replace(/\r\n/g, "\n").split("\n");
    const delimiterIndex = lines.findIndex((l) => l.trim() === "---");
    if (delimiterIndex === -1) return { summary: text.trim(), meta: [], details: "" };

    const summary = lines.slice(0, delimiterIndex).join("\n").trim();
    const metaLines = lines.slice(delimiterIndex + 1);
    const meta = [];
    const rest = [];
    for (const line of metaLines) {
      const m = line.match(/^\*\*(.+?)\*\*:\s*(.*)\s*$/);
      if (m) meta.push({ key: m[1].trim(), value: m[2].trim() });
      else rest.push(line);
    }
    return { summary, meta, details: rest.join("\n").trim() };
  };

  const extractImageUrls = (raw) => {
    const text = raw == null ? "" : String(raw);
    if (!text.trim()) return [];

    const urls = [];
    const seen = new Set();
    const push = (value) => {
      const v = String(value || "").trim();
      if (!v) return;
      if (seen.has(v)) return;
      seen.add(v);
      urls.push(v);
    };

    try {
      const mdImg = /!\[[^\]]*\]\(([^)\s]+)(?:\s+\"[^\"]*\")?\)/g;
      let m;
      while ((m = mdImg.exec(text))) push(m[1]);
    } catch {
      // ignore
    }

    try {
      const dataImg = /(data:image\/[a-zA-Z0-9.+-]+;base64,[A-Za-z0-9+/=]+)\b/g;
      let m;
      while ((m = dataImg.exec(text))) push(m[1]);
    } catch {
      // ignore
    }

    try {
      const httpImg = /(https?:\/\/[^\s)]+?\.(?:png|jpe?g|gif|webp))(?:\?[^\s)]*)?/gi;
      let m;
      while ((m = httpImg.exec(text))) push(m[0]);
    } catch {
      // ignore
    }

    return urls;
  };

  const normalizeAssets = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.assets)) return data.assets;
    if (Array.isArray(data?.items)) return data.items;
    if (Array.isArray(data?.content)) return data.content;
    return [];
  };

  const assetDisplay = (asset) => {
    if (!asset || typeof asset !== "object") return { id: "", name: "", url: "", contentType: "" };
    const id = asset.id ?? asset.assetId ?? asset.attachmentId ?? asset.fileId ?? "";
    const name = asset.name || asset.fileName || asset.filename || asset.path || "";
    const contentType = asset.contentType || asset.mimeType || asset.type || "";
    const url = asset.downloadUrl || asset.browserDownloadUrl || asset.url || asset.href || "";
    return {
      id: String(id ?? ""),
      name: String(name || ""),
      url: String(url || ""),
      contentType: String(contentType || ""),
    };
  };

  const isImageAsset = ({ name, contentType, url }) => {
    const ct = String(contentType || "").toLowerCase();
    if (ct.startsWith("image/")) return true;
    const target = String(name || url || "").toLowerCase();
    return /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(target);
  };

  const buildProxyAssetUrl = (baseURL, issueNumber, assetId) => {
    const b = baseURL == null ? "" : String(baseURL).replace(/\/+$/, "");
    const n = issueNumber == null ? "" : String(issueNumber).trim();
    const id = assetId == null ? "" : String(assetId).trim();
    if (!b || !n || !id) return "";
    return `${b}/issues/${encodeURIComponent(n)}/assets/${encodeURIComponent(id)}`;
  };

  const isProtectedAssetUrl = (url, baseURL) => {
    const u = String(url || "").trim();
    if (!u) return false;
    const b = String(baseURL || "").replace(/\/+$/, "");
    if (b && u.startsWith(b)) return true;
    if (u.includes("/api/v1/issues/") && u.includes("/assets/")) return true;
    // Treat any issue-assets style URL as protected (proxy endpoint requires Bearer auth).
    if (u.includes("/issues/") && u.includes("/assets/")) return true;
    return /\/issues\/[^/]+\/assets\/[^/?#]+(?:[/?#]|$)/.test(u);
  };

  const getMetaValue = (meta, key) => {
    const needle = String(key || "").trim().toLowerCase();
    if (!needle) return "";
    const found = (Array.isArray(meta) ? meta : []).find(
      (row) => String(row?.key || "").trim().toLowerCase() === needle
    );
    return found?.value != null ? String(found.value) : "";
  };

  const formatReporter = (value) => {
    if (!value) return "-";
    if (typeof value === "string") return value;
    if (typeof value === "object") {
      return (
        value?.username ||
        value?.login ||
        value?.name ||
        value?.displayName ||
        value?.fullName ||
        value?.email ||
        "-"
      );
    }
    return String(value);
  };

  const formatReporterEmail = (value) => {
    if (!value) return "";
    if (typeof value === "string") return value.includes("@") ? value : "";
    if (typeof value === "object") {
      const email = value?.email || value?.mail || "";
      return typeof email === "string" && email.includes("@") ? email : "";
    }
    return "";
  };

  const workflowChipClass = (status) => {
    const normalized = String(status || "").trim().toLowerCase();
    if (!normalized) return "bg-slate-500/10 text-slate-700 dark:text-slate-200";
    if (["new", "nieuw"].includes(normalized)) return "bg-sky-500/15 text-sky-700 dark:text-sky-200";
    if (["in progress", "in behandeling"].includes(normalized))
      return "bg-amber-500/15 text-amber-800 dark:text-amber-200";
    if (["on hold", "in wacht"].includes(normalized))
      return "bg-rose-500/15 text-rose-700 dark:text-rose-200";
    if (["rejected", "afgewezen"].includes(normalized))
      return "bg-rose-500/15 text-rose-700 dark:text-rose-200";
    if (["closed", "gesloten"].includes(normalized))
      return "bg-slate-500/15 text-slate-700 dark:text-slate-200";
    if (["done", "resolved", "opgelost"].includes(normalized))
      return "bg-emerald-500/15 text-emerald-700 dark:text-emerald-200";
    if (["blocked", "geblokkeerd"].includes(normalized))
      return "bg-rose-500/15 text-rose-700 dark:text-rose-200";
    return "bg-indigo-500/15 text-indigo-700 dark:text-indigo-200";
  };

  const workflowStatusLabel = (value) => {
    const raw = value == null ? "" : String(value).trim();
    const normalized = raw.toLowerCase();
    if (!raw) return "";
    if (normalized === "new" || normalized === "nieuw")
      return t("gitea.workflow_status.new", { defaultValue: "Nieuw" });
    if (normalized === "in progress" || normalized === "in behandeling")
      return t("gitea.workflow_status.in_progress", { defaultValue: "In behandeling" });
    if (normalized === "on hold" || normalized === "in wacht")
      return t("gitea.workflow_status.on_hold", { defaultValue: "In wacht" });
    if (normalized === "closed" || normalized === "gesloten")
      return t("gitea.workflow_status.closed", { defaultValue: "Gesloten" });
    if (normalized === "rejected" || normalized === "afgewezen")
      return t("gitea.workflow_status.rejected", { defaultValue: "Afgewezen" });
    return raw;
  };

  const extractComments = (data) => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    const candidates = [data.comments, data.items, data.entries, data.content].filter(Array.isArray);
    if (candidates.length > 0) return candidates[0];
    if (data.issue && (Array.isArray(data.issue.comments) || Array.isArray(data.issue.items))) {
      return data.issue.comments || data.issue.items;
    }
    return [];
  };

  const closeIssueDrawer = () => {
    setIssueDrawerOpen(false);
    setActiveIssue(null);
    setIssueDetails(null);
    setIssueDetailsError(null);
    setIssueDetailsLoading(false);
    setIssueComments([]);
    setIssueCommentsError(null);
    setIssueCommentsLoading(false);
    setIssueAssets([]);
    setIssueAssetsError(null);
    setIssueAssetsLoading(false);
    setIssueAssetObjectUrls((prev) => {
      Object.values(prev || {}).forEach((u) => {
        try {
          if (typeof u === "string" && u) URL.revokeObjectURL(u);
        } catch {
          // ignore
        }
      });
      return {};
    });
    setNewIssueComment("");
    setNewIssueCommentError(null);
    setNewIssueCommentSaving(false);
    setIssueDrawerShowDefaultRoles(false);
  };

  const openIssueDrawer = async (issue) => {
    setActiveIssue(issue || null);
    setIssueDrawerOpen(true);
    setIssueDetails(null);
    setIssueDetailsError(null);
    setIssueComments([]);
    setIssueCommentsError(null);
    setNewIssueComment("");
    setNewIssueCommentError(null);
    setIssueDrawerShowDefaultRoles(false);

    const number = issue?.number ?? issue?.issueNumber ?? issue?.id ?? issue?.issue_id ?? null;
    if (number == null) return;

    setIssueDetailsLoading(true);
    try {
      const res = await http.get(`/issues/${encodeURIComponent(number)}`);
      setIssueDetails(res?.data || null);
    } catch (e) {
      setIssueDetails(null);
      setIssueDetailsError(mapHttpErrorToMessage(e, t) || e);
    } finally {
      setIssueDetailsLoading(false);
    }

    setIssueAssetsLoading(true);
    try {
      setIssueAssetObjectUrls((prev) => {
        Object.values(prev || {}).forEach((u) => {
          try {
            if (typeof u === "string" && u) URL.revokeObjectURL(u);
          } catch {
            // ignore
          }
        });
        return {};
      });
      const res = await http.get(`/issues/${encodeURIComponent(number)}/assets`);
      setIssueAssets(normalizeAssets(res?.data));
    } catch (e) {
      setIssueAssets([]);
      setIssueAssetsError(mapHttpErrorToMessage(e, t) || e);
    } finally {
      setIssueAssetsLoading(false);
    }

    setIssueCommentsLoading(true);
    try {
      const res = await http.get(`/issues/${encodeURIComponent(number)}/comments`);
      const comments = extractComments(res?.data);
      setIssueComments(comments);
    } catch (e) {
      setIssueComments([]);
      setIssueCommentsError(mapHttpErrorToMessage(e, t) || e);
    } finally {
      setIssueCommentsLoading(false);
    }
  };

  const openIssueAsset = async (url) => {
    const u = String(url || "").trim();
    if (!u) return;
    const existing = issueAssetObjectUrls?.[u];
    if (existing) {
      window.open(existing, "_blank") || window.open(existing, "_self");
      return;
    }
    const popup = window.open("", "_blank");
    try {
      if (popup?.document) {
        popup.document.title = "Bijlage laden…";
        popup.document.body.innerHTML =
          '<div style="font-family:system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;padding:24px;color:#0f172a">' +
          "<h2 style=\"margin:0 0 8px;font-size:18px\">Bijlage laden…</h2>" +
          "<p style=\"margin:0;font-size:14px;color:#475569\">Even geduld.</p>" +
          "</div>";
      }
    } catch {
      // ignore
    }
    try {
      const res = await http.get(u, { responseType: "blob" });
      const blob = res?.data;
      if (!blob) return;
      const objectUrl = URL.createObjectURL(blob);
      setIssueAssetObjectUrls((prev) => ({ ...(prev || {}), [u]: objectUrl }));
      if (popup && !popup.closed) {
        popup.location.href = objectUrl;
      } else {
        window.open(objectUrl, "_self");
      }
    } catch (e) {
      const rawMessage = mapHttpErrorToMessage(e, t) || "Kon bijlage niet laden.";
      const parts = [];
      if (typeof rawMessage === "string") parts.push(rawMessage);
      else {
        try {
          parts.push(JSON.stringify(rawMessage, null, 2));
        } catch {
          parts.push(String(rawMessage));
        }
      }
      try {
        const status = e?.response?.status;
        if (status) parts.push(`HTTP ${status}`);
        const data = e?.response?.data;
        if (data != null && typeof data !== "string") parts.push(JSON.stringify(data, null, 2));
        else if (typeof data === "string" && data.trim()) parts.push(data.trim());
      } catch {
        // ignore
      }
      const message = parts.filter(Boolean).join("\n\n");
      try {
        if (popup && !popup.closed && popup.document) {
          popup.document.title = "Bijlage niet beschikbaar";
          popup.document.body.innerHTML =
            '<div style="font-family:system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;padding:24px;color:#0f172a">' +
            "<h2 style=\"margin:0 0 8px;font-size:18px\">Bijlage niet beschikbaar</h2>" +
            `<pre style="white-space:pre-wrap;margin:0;font-size:13px;color:#b91c1c">${String(message || "").replace(
              /</g,
              "&lt;"
            )}</pre>` +
            "</div>";
        }
      } catch {
        // ignore
      }
    }
  };

  useEffect(() => {
    if (!issueDrawerOpen) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") closeIssueDrawer();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issueDrawerOpen]);

  const normalizeIssueStatus = (value) => String(value || "").trim().toLowerCase();

  const filteredMyIssues = React.useMemo(() => {
    const filter = normalizeIssueStatus(myIssuesStatusFilter);
    if (!filter || filter === "all") return myIssues;
    return myIssues.filter((it) => {
      const status = normalizeIssueStatus(it?.status ?? it?.state ?? it?.phase ?? "");
      return status === filter;
    });
  }, [myIssues, myIssuesStatusFilter]);

  const submitIssueComment = async () => {
    const issue = activeIssue;
    const number = issue?.number ?? issue?.issueNumber ?? issue?.id ?? issue?.issue_id ?? null;
    const body = String(newIssueComment || "").trim();
    if (number == null || !body) return;

    setNewIssueCommentSaving(true);
    setNewIssueCommentError(null);
    try {
      await http.post(`/issues/${encodeURIComponent(number)}/comments`, { body });
      setNewIssueComment("");
      await openIssueDrawer(issue);
    } catch (e) {
      setNewIssueCommentError(mapHttpErrorToMessage(e, t) || e);
    } finally {
      setNewIssueCommentSaving(false);
    }
  };

  const sortedIssueComments = React.useMemo(() => {
    const list = Array.isArray(issueComments) ? issueComments : [];
    return [...list].sort((a, b) => {
      const ad = parseIssueCreatedAt(a?.createdAt ?? a?.created_at ?? a?.created ?? a?.timestamp ?? null);
      const bd = parseIssueCreatedAt(b?.createdAt ?? b?.created_at ?? b?.created ?? b?.timestamp ?? null);
      const at = ad ? ad.getTime() : Infinity;
      const bt = bd ? bd.getTime() : Infinity;
      if (at !== bt) return at - bt;
      return String(a?.id ?? "").localeCompare(String(b?.id ?? ""), undefined, { numeric: true });
    });
  }, [issueComments]);

  const resolvedIssueAssets = React.useMemo(() => {
    const baseURL = http?.defaults?.baseURL || "";
    const issueNumber =
      issueDetails?.number ?? activeIssue?.number ?? activeIssue?.issueNumber ?? activeIssue?.id ?? null;
    return (Array.isArray(issueAssets) ? issueAssets : [])
      .map(assetDisplay)
      .map((a) => {
        const proxyUrl = buildProxyAssetUrl(baseURL, issueNumber, a.id);
        const resolvedUrl = proxyUrl || a.url || "";
        return { ...a, resolvedUrl };
      })
      .filter((a) => a.resolvedUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issueAssets, issueDetails, activeIssue]);

  const issueImageAssets = React.useMemo(() => resolvedIssueAssets.filter((a) => isImageAsset(a)), [resolvedIssueAssets]);
  const issueOtherAssets = React.useMemo(() => resolvedIssueAssets.filter((a) => !isImageAsset(a)), [resolvedIssueAssets]);

  useEffect(() => {
    if (!issueDrawerOpen) return;
    if (issueImageAssets.length === 0) return;
    let cancelled = false;
    const toFetch = issueImageAssets
      .map((a) => a.resolvedUrl)
      .filter((u) => u && !String(u).startsWith("data:"))
      .filter((u) => !issueAssetObjectUrls?.[u]);
    if (toFetch.length === 0) return;

    const concurrency = 3;
    let idx = 0;
    const workers = Array.from({ length: Math.min(concurrency, toFetch.length) }).map(async () => {
      while (idx < toFetch.length && !cancelled) {
        const url = toFetch[idx++];
        try {
          const res = await http.get(url, { responseType: "blob" });
          const blob = res?.data;
          if (!blob) continue;
          const objectUrl = URL.createObjectURL(blob);
          if (cancelled) {
            try {
              URL.revokeObjectURL(objectUrl);
            } catch {
              // ignore
            }
            continue;
          }
          setIssueAssetObjectUrls((prev) => ({ ...(prev || {}), [url]: objectUrl }));
        } catch {
          // ignore
        }
      }
    });
    Promise.all(workers);
    return () => {
      cancelled = true;
    };
  }, [issueDrawerOpen, issueImageAssets, issueAssetObjectUrls]);

  // Init: profiel ophalen en locale bepalen
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        await keycloak.updateToken(30);
        const { data } = await axios.get(accountApiUrl, {
          headers: {
            Authorization: `Bearer ${keycloak.token}`,
            Accept: "application/json",
          },
        });
        setProfile(data || {});
        const picked = (pickLocale(keycloak, data) || "en").toLowerCase();
        setLocale(picked.startsWith("nl") ? "nl" : "en");
        setStartPage(pickStartPagePreference(data, keycloak?.tokenParsed) || START_PAGE_DASHBOARD);
      } catch (e) {
        setError(t("error_message", "Something went wrong. Please try again."));
      } finally {
        setLoading(false);
      }
    };
    if (keycloak?.token) fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keycloak?.token]);

  useEffect(() => {
    if (variant !== "staff") return;
    if (!keycloak?.token) return;
    let active = true;
    const fetchMine = async () => {
      setMyIssuesLoaded(false);
      setMyIssuesLoading(true);
      setMyIssuesError(null);
      try {
        const { data } = await http.get("/issues/mine");
        const items = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : Array.isArray(data?.content) ? data.content : [];
        if (!active) return;
        setMyIssues(items);
        workflowEnrichedKeyRef.current = "";
      } catch (e) {
        if (!active) return;
        setMyIssues([]);
        setMyIssuesError(mapHttpErrorToMessage(e, t) || e);
      } finally {
        if (!active) return;
        setMyIssuesLoading(false);
        setMyIssuesLoaded(true);
      }
    };
    fetchMine();
    return () => {
      active = false;
    };
  }, [variant, keycloak?.token, t]);

  useEffect(() => {
    if (variant !== "staff") return;
    if (!keycloak?.token) return;
    if (!myIssuesLoaded || myIssuesLoading) return;

    const raw = location?.search || "";
    if (!raw) return;
    const params = new URLSearchParams(raw);
    const issueParam = params.get("issue");
    const trimmed = String(issueParam || "").trim();
    if (!/^[0-9]+$/.test(trimmed)) return;

    const targetIssue = (Array.isArray(myIssues) ? myIssues : []).find((issue) => {
      const value = issue?.number ?? issue?.issueNumber ?? issue?.id ?? issue?.issue_id ?? null;
      return value != null && String(value) === trimmed;
    });

    const activeNumber =
      activeIssue?.number ?? activeIssue?.issueNumber ?? activeIssue?.id ?? activeIssue?.issue_id ?? null;
    if (issueDrawerOpen && String(activeNumber) === trimmed) return;

    setActiveProfileSection("issues");
    setMyIssuesStatusFilter("all");
    openIssueDrawer(targetIssue || { number: trimmed });
    navigate("/profile", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    variant,
    keycloak?.token,
    location?.search,
    myIssuesLoaded,
    myIssuesLoading,
    myIssues,
    issueDrawerOpen,
    activeIssue,
    navigate,
  ]);

  useEffect(() => {
    if (variant !== "staff") return;
    if (myIssuesLoading) return;
    if (!Array.isArray(myIssues) || myIssues.length === 0) return;

    const numbers = myIssues
      .map((it) => it?.number ?? it?.issueNumber ?? it?.id ?? it?.issue_id ?? null)
      .filter((n) => n != null)
      .map((n) => String(n))
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
      .join(",");
    const key = `${numbers}`;
    if (workflowEnrichedKeyRef.current === key) return;
    workflowEnrichedKeyRef.current = key;

    let cancelled = false;
    const enrich = async () => {
      const missing = myIssues
        .filter((it) => !(it?.workflowStatus ?? it?.workflow_status))
        .map((it) => it?.number ?? it?.issueNumber ?? it?.id ?? it?.issue_id ?? null)
        .filter((n) => n != null)
        .map((n) => String(n));
      if (missing.length === 0) return;

      const byNumber = {};
      const concurrency = 4;
      let idx = 0;
      const workers = Array.from({ length: Math.min(concurrency, missing.length) }).map(async () => {
        while (idx < missing.length) {
          const n = missing[idx++];
          try {
            const res = await http.get(`/issues/${encodeURIComponent(n)}`);
            const ws = res?.data?.workflowStatus || "";
            if (ws) byNumber[n] = ws;
          } catch {
            // ignore
          }
        }
      });
      await Promise.all(workers);
      if (cancelled) return;
      if (Object.keys(byNumber).length === 0) return;
      setMyIssues((prev) =>
        (Array.isArray(prev) ? prev : []).map((it) => {
          const n = it?.number ?? it?.issueNumber ?? it?.id ?? it?.issue_id ?? null;
          const k = n == null ? null : String(n);
          const ws = k ? byNumber[k] : "";
          return ws ? { ...it, workflowStatus: it?.workflowStatus ?? ws } : it;
        })
      );
    };

    enrich();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant, myIssuesLoading, myIssues]);

  // Opslaan in Keycloak + i18n updaten
  const saveLocale = async () => {
    if (!profile) return;
    setSaving(true);
    setSaved(false);
    setError("");

    try {
      await keycloak.updateToken(30);

      const payload = {
        firstName: profile.firstName ?? "",
        lastName: profile.lastName ?? "",
        email: profile.email ?? "",
        attributes: withStartPagePreference({
          ...(profile.attributes || {}),
          locale: [locale],
          preferred_language: [locale],
        }, effectiveStartPage),
      };

      const resp = await axios.post(accountApiUrl, payload, {
        headers: {
          Authorization: `Bearer ${keycloak.token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        validateStatus: () => true,
      });

      if (resp.status === 200 || resp.status === 204) {
        i18n.changeLanguage(locale);
        await keycloak.updateToken(0);
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      } else {
        const msg = resp.data?.error_description || resp.data?.error || `HTTP ${resp.status}`;
        throw new Error(msg);
      }
    } catch (e) {
      setError(
        `${t("profile.preferences_save_failed", "Saving your preferences failed. Please try again.")}${
          e?.message ? ` (${e.message})` : ""
        }`
      );
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordInput = (field, value) => {
    setPasswordForm((prev) => ({ ...prev, [field]: value }));
    setPasswordError("");
    setPasswordSaved(false);
  };

  const openPasswordModal = () => {
    setPasswordModalOpen(true);
    setPasswordError("");
    setPasswordSaved(false);
  };

  const closePasswordModal = () => {
    if (passwordSaving) return;
    setPasswordModalOpen(false);
    setPasswordForm({ current: "", next: "", confirm: "" });
    setPasswordError("");
    setPasswordSaved(false);
  };

  const savePassword = async () => {
    setPasswordError("");
    setPasswordSaved(false);
    if (!passwordForm.current || !passwordForm.next || !passwordForm.confirm) {
      setPasswordError(t("profile.password.missing", "Please fill in all password fields."));
      return;
    }
    if (passwordForm.next !== passwordForm.confirm) {
      setPasswordError(t("profile.password.mismatch", "New passwords do not match."));
      return;
    }
    if (passwordForm.next.length < 8) {
      setPasswordError(t("profile.password.too_short", "Choose a password with at least 8 characters."));
      return;
    }
    setPasswordSaving(true);
    try {
      await keycloak.updateToken(30);
      const resp = await axios.post(
        passwordApiUrl,
        {
          currentPassword: passwordForm.current,
          newPassword: passwordForm.next,
        },
        {
          headers: {
            Authorization: `Bearer ${keycloak.token}`,
          },
          validateStatus: () => true,
        }
      );

      if (resp.status === 204 || resp.status === 200) {
        setPasswordSaved(true);
        setPasswordForm({ current: "", next: "", confirm: "" });
        setTimeout(() => {
          setPasswordSaved(false);
          setPasswordModalOpen(false);
        }, 1200);
      } else {
        const msg =
          resp.data?.error_description ||
          resp.data?.error ||
          resp.data?.message ||
          t("profile.password.save_failed", "Password change failed.");
        throw new Error(msg);
      }
    } catch (e) {
      setPasswordError(
        e?.message || t("profile.password.save_failed", "Password change failed.")
      );
    } finally {
      setPasswordSaving(false);
    }
  };

  const profileFirstName = keycloak.idTokenParsed?.given_name || profile?.firstName || "";
  const profileLastName = keycloak.idTokenParsed?.family_name || profile?.lastName || "";
  const profileEmail = keycloak.idTokenParsed?.email || profile?.email || "";
  const profileDisplayName = [profileFirstName, profileLastName].filter(Boolean).join(" ") || username;
  const profileInitials =
    [profileFirstName, profileLastName]
      .filter(Boolean)
      .map((part) => String(part).trim().charAt(0))
      .join("")
      .slice(0, 2)
      .toUpperCase() || String(username || "?").charAt(0).toUpperCase();
  const currentDateExample = new Date().toLocaleDateString(i18n.language || "nl-NL", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  if (loading) {
    return (
      <div className={wrapperClasses}>
        <div className="p-6 text-gray-900 dark:text-gray-200">
          {t("common.loading", "Loading...")}
        </div>
      </div>
    );
  }

  if (isParentDesktop) {
    return (
      <>
        <div className="grid gap-5 text-slate-950 dark:text-slate-100 xl:grid-cols-[minmax(0,1.05fr)_minmax(22rem,0.95fr)] xl:items-start">
          <section className="rounded-[2rem] border border-slate-200 bg-white/80 p-5 shadow-sm dark:border-slate-800 dark:bg-slate-950/30 sm:p-6">
            <div className="mb-5">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-700 dark:text-emerald-300">
                {t("parentPortal.profile.account", { defaultValue: "Account" })}
              </p>
              <h2 className="mt-1 text-xl font-black tracking-tight text-slate-950 dark:text-slate-50">
                {t("profile.title", { defaultValue: "Profile" })}
              </h2>
            </div>

            <div className="grid gap-4">
              <div>
                <label className="text-sm font-semibold text-slate-600 dark:text-slate-300">
                  {t("profile.username", { defaultValue: "Username" })}
                </label>
                <div className="mt-1 min-h-[44px] rounded-2xl bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-900 ring-1 ring-slate-100 dark:bg-slate-900/60 dark:text-slate-100 dark:ring-slate-800">
                  {username}
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-sm font-semibold text-slate-600 dark:text-slate-300">
                    {t("profile.first_name", { defaultValue: "First name" })}
                  </label>
                  <div className="mt-1 min-h-[44px] rounded-2xl bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-900 ring-1 ring-slate-100 dark:bg-slate-900/60 dark:text-slate-100 dark:ring-slate-800">
                    {keycloak.idTokenParsed?.given_name || profile?.firstName || "N/A"}
                  </div>
                </div>

                <div>
                  <label className="text-sm font-semibold text-slate-600 dark:text-slate-300">
                    {t("profile.last_name", { defaultValue: "Last name" })}
                  </label>
                  <div className="mt-1 min-h-[44px] rounded-2xl bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-900 ring-1 ring-slate-100 dark:bg-slate-900/60 dark:text-slate-100 dark:ring-slate-800">
                    {keycloak.idTokenParsed?.family_name || profile?.lastName || "N/A"}
                  </div>
                </div>
              </div>

              <div>
                <label className="text-sm font-semibold text-slate-600 dark:text-slate-300">
                  {t("profile.email", { defaultValue: "Email" })}
                </label>
                <div className="mt-1 min-h-[44px] rounded-2xl bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-900 ring-1 ring-slate-100 dark:bg-slate-900/60 dark:text-slate-100 dark:ring-slate-800">
                  {keycloak.idTokenParsed?.email || profile?.email || "N/A"}
                </div>
              </div>

              <div>
                <label className="text-sm font-semibold text-slate-600 dark:text-slate-300">
                  {t("profile.language", { defaultValue: "Language" })}
                </label>
                <select
                  value={locale}
                  onChange={(e) => setLocale(e.target.value)}
                  className="mt-1 min-h-[44px] w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-900 outline-none transition focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100 dark:border-slate-700 dark:bg-slate-950/40 dark:text-slate-100 dark:focus:ring-emerald-900/30"
                >
                  {AVAILABLE_LOCALES.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {canChooseStartPage && (
                <StartPagePreferenceField t={t} value={effectiveStartPage} options={startPageOptions} onChange={setStartPage} />
              )}

              {saved && (
                <p className="rounded-2xl bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800 ring-1 ring-emerald-100 dark:bg-emerald-950/30 dark:text-emerald-100 dark:ring-emerald-900/50">
                  {t("profile.saved_preferences", "Preferences saved.")}
                </p>
              )}

              {error && (
                <p className="rounded-2xl bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700 ring-1 ring-rose-100 dark:bg-rose-950/30 dark:text-rose-100 dark:ring-rose-900/50">
                  {error}
                </p>
              )}

              <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:items-center sm:justify-between">
                <button
                  type="button"
                  onClick={openPasswordModal}
                  className={secondaryButtonClass}
                >
                  {t("profile.password.open_modal", "Change password")}
                </button>
                <button
                  type="button"
                  onClick={saveLocale}
                  disabled={saving}
                  className={primaryButtonClass}
                >
                  {saving ? t("common.saving", "Saving…") : t("common.save", "Save")}
                </button>
              </div>
            </div>
          </section>

          <section className="rounded-[2rem] border border-slate-200 bg-white/80 p-5 shadow-sm dark:border-slate-800 dark:bg-slate-950/30 sm:p-6">
            <NotificationPreferencesSection topicsConfig={parentTopicsConfig} presentation="embedded" />
          </section>
        </div>

        <Modal
          show={passwordModalOpen}
          onClose={closePasswordModal}
          title={t("profile.password.title", "Change password")}
          type="password"
          hideDefaultActions
        >
          <div className="text-left space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {t(
                "profile.password.subtitle",
                "Update your password. Make sure it is unique and not shared elsewhere."
              )}
            </p>
            <div>
              <label className="block text-gray-700 dark:text-gray-300">
                {t("profile.password.current", "Current password")}
              </label>
              <input
                type="password"
                value={passwordForm.current}
                onChange={(e) => {
                  setPasswordError("");
                  setPasswordSaved(false);
                  setPasswordForm((prev) => ({ ...prev, current: e.target.value }));
                }}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                autoComplete="current-password"
              />
            </div>
            <div>
              <label className="block text-gray-700 dark:text-gray-300">
                {t("profile.password.new", "New password")}
              </label>
              <input
                type="password"
                value={passwordForm.next}
                onChange={(e) => {
                  setPasswordError("");
                  setPasswordSaved(false);
                  setPasswordForm((prev) => ({ ...prev, next: e.target.value }));
                }}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                autoComplete="new-password"
              />
            </div>
            <div>
              <label className="block text-gray-700 dark:text-gray-300">
                {t("profile.password.confirm", "Confirm new password")}
              </label>
              <input
                type="password"
                value={passwordForm.confirm}
                onChange={(e) => {
                  setPasswordError("");
                  setPasswordSaved(false);
                  setPasswordForm((prev) => ({ ...prev, confirm: e.target.value }));
                }}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                autoComplete="new-password"
              />
            </div>

            {passwordError && (
              <p className="text-sm text-red-600 dark:text-red-400">{passwordError}</p>
            )}
            {passwordSaved && (
              <p className="text-sm text-green-700 dark:text-green-300">
                {t("profile.password.saved", "Password updated.")}
              </p>
            )}

            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button type="button" onClick={closePasswordModal} className={secondaryButtonClass}>
                {t("common.cancel", "Cancel")}
              </button>
              <button
                type="button"
                onClick={savePassword}
                disabled={passwordSaving}
                className={primaryButtonClass}
              >
                {passwordSaving ? t("profile.password.saving", "Updating…") : t("profile.password.save", "Update password")}
              </button>
            </div>
          </div>
        </Modal>
      </>
    );
  }

  return (
    <>
      <div className={wrapperClasses}>
        {variant === "staff" ? (
          <div className="grid gap-6 xl:grid-cols-[260px_minmax(0,1fr)]">
            <aside className="lds-overview-card-enter flex flex-col rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-slate-900/80">
              <nav className="space-y-1.5">
                {[
                  { id: "profile", icon: FaUserCircle, label: t("profile.nav.profile", { defaultValue: "Profielgegevens" }) },
                  { id: "security", icon: FaShieldAlt, label: t("profile.nav.security", { defaultValue: "Beveiliging" }) },
                  { id: "preferences", icon: FaGlobe, label: t("profile.nav.preferences", { defaultValue: "Voorkeuren" }) },
                  { id: "notifications", icon: FaBell, label: t("profile.nav.notifications", { defaultValue: "Notificaties" }) },
                  { id: "issues", icon: FaTicketAlt, label: t("profile.my_issues.title", { defaultValue: "Mijn issues" }), separated: true },
                ].map((item) => {
                  const Icon = item.icon;
                  const active = activeProfileSection === item.id;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setActiveProfileSection(item.id)}
                      className={`flex w-full items-center gap-3 rounded-2xl px-4 text-left text-sm font-bold transition ${
                        active
                          ? "border-l-4 border-indigo-600 bg-indigo-50 text-indigo-700 dark:bg-indigo-950/35 dark:text-indigo-200"
                          : "text-slate-600 hover:bg-slate-50 hover:text-slate-950 dark:text-slate-300 dark:hover:bg-white/[0.04] dark:hover:text-white"
                      } ${item.separated ? "mt-8 min-h-[58px]" : "min-h-[48px]"}`}
                    >
                      <Icon className="text-lg" />
                      <span>{item.label}</span>
                    </button>
                  );
                })}
              </nav>

              <div className="mt-8 rounded-2xl border border-indigo-100 bg-sky-50/70 p-4 dark:border-indigo-500/30 dark:bg-indigo-950/20">
                <div className="flex items-start gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-200">
                    <FaShieldAlt />
                  </div>
                  <div>
                    <div className="text-sm font-bold text-slate-900 dark:text-white">
                      {t("profile.account_active", { defaultValue: "Account actief" })}
                    </div>
                    <p className="mt-1 text-xs text-slate-600 dark:text-slate-300">
                      {t("profile.account_active_hint", { defaultValue: "Je account is in goede staat." })}
                    </p>
                  </div>
                </div>
              </div>
            </aside>

            <div className="lds-overview-card-enter space-y-6" style={{ animationDelay: "100ms" }}>
              {activeProfileSection === "profile" && (
              <section id="profile-info" className={cardClasses}>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <h2 className="text-xl font-bold text-slate-950 dark:text-white">
                      {t("profile.details", { defaultValue: "Profielgegevens" })}
                    </h2>
                    <div className="mt-5 flex items-center gap-5">
                      <div className="flex h-24 w-24 shrink-0 items-center justify-center rounded-full bg-violet-100 text-3xl font-black text-indigo-700 dark:bg-violet-900/40 dark:text-violet-200">
                        {profileInitials}
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-slate-950 dark:text-white">{profileDisplayName}</div>
                        <div className="mt-1 text-sm font-semibold text-slate-500 dark:text-slate-300">{profileEmail || username}</div>
                        <div className="mt-2 inline-flex items-center gap-2 text-sm font-bold text-emerald-700 dark:text-emerald-300">
                          <FaShieldAlt />
                          {t("profile.account_active", { defaultValue: "Account actief" })}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <button type="button" className={secondaryButtonClass}>
                      {t("common.cancel", { defaultValue: "Annuleren" })}
                    </button>
                    <button type="button" onClick={saveLocale} disabled={saving} className={primaryButtonClass}>
                      {saving
                        ? t("common.saving", { defaultValue: "Saving…" })
                        : t("profile.save_changes", { defaultValue: "Wijzigingen opslaan" })}
                    </button>
                  </div>
                </div>

                <div className="mt-6 grid gap-4 md:grid-cols-2">
                  <label className="block text-sm font-semibold text-slate-600 dark:text-slate-300">
                    {t("profile.first_name", "First name")}
                    <input
                      value={profileFirstName || "N/A"}
                      readOnly
                      className="mt-2 min-h-[44px] w-full rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-900 shadow-sm dark:border-slate-700 dark:bg-slate-950/40 dark:text-slate-100"
                    />
                  </label>
                  <label className="block text-sm font-semibold text-slate-600 dark:text-slate-300">
                    {t("profile.last_name", "Last name")}
                    <input
                      value={profileLastName || "N/A"}
                      readOnly
                      className="mt-2 min-h-[44px] w-full rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-900 shadow-sm dark:border-slate-700 dark:bg-slate-950/40 dark:text-slate-100"
                    />
                  </label>
                  <label className="block text-sm font-semibold text-slate-600 dark:text-slate-300 md:col-span-2">
                    {t("profile.email", "Email")}
                    <input
                      value={profileEmail || "N/A"}
                      readOnly
                      className="mt-2 min-h-[44px] w-full rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-900 shadow-sm dark:border-slate-700 dark:bg-slate-950/40 dark:text-slate-100"
                    />
                  </label>
                  <label className="block text-sm font-semibold text-slate-600 dark:text-slate-300 md:col-span-2">
                    {t("profile.username", "Username")} ({t("common.readonly", { defaultValue: "niet aanpasbaar" })})
                    <input
                      value={username}
                      readOnly
                      className="mt-2 min-h-[44px] w-full rounded-xl border border-slate-200 bg-slate-50 px-4 text-sm font-semibold text-slate-500 shadow-sm dark:border-slate-700 dark:bg-slate-900/60 dark:text-slate-300"
                    />
                  </label>
                </div>

                {saved && (
                  <p className="mt-4 rounded-2xl bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800 ring-1 ring-emerald-100 dark:bg-emerald-950/30 dark:text-emerald-100 dark:ring-emerald-900/50">
                    {t("profile.saved_preferences", "Preferences saved.")}
                  </p>
                )}
                {error && (
                  <p className="mt-4 rounded-2xl bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700 ring-1 ring-rose-100 dark:bg-rose-950/30 dark:text-rose-100 dark:ring-rose-900/50">
                    {error}
                  </p>
                )}
              </section>
              )}

              {activeProfileSection === "preferences" && (
                <section id="preferences" className={cardClasses}>
                  <div className="mb-5 flex items-center gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-200">
                      <FaGlobe />
                    </div>
                    <h3 className="text-lg font-bold text-slate-950 dark:text-white">
                      {t("profile.nav.preferences", { defaultValue: "Voorkeuren" })}
                    </h3>
                  </div>
                  <div className="space-y-4">
                    <label className="block text-sm font-semibold text-slate-600 dark:text-slate-300">
                      {t("profile.language", "Language")}
                      <select
                        value={locale}
                        onChange={(e) => setLocale(e.target.value)}
                        className="mt-2 min-h-[44px] w-full rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-900 shadow-sm focus:border-indigo-400 focus:outline-none focus:ring-4 focus:ring-indigo-100 dark:border-slate-700 dark:bg-slate-950/40 dark:text-slate-100 dark:focus:ring-indigo-900/40"
                      >
                        {AVAILABLE_LOCALES.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    {canChooseStartPage && (
                      <StartPagePreferenceField t={t} value={effectiveStartPage} options={startPageOptions} onChange={setStartPage} />
                    )}
                    <label className="block text-sm font-semibold text-slate-600 dark:text-slate-300">
                      {t("profile.timezone", { defaultValue: "Tijdzone" })}
                      <select
                        value="Europe/Amsterdam"
                        onChange={() => undefined}
                        className="mt-2 min-h-[44px] w-full rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-900 shadow-sm dark:border-slate-700 dark:bg-slate-950/40 dark:text-slate-100"
                      >
                        <option value="Europe/Amsterdam">(UTC+01:00) Amsterdam, Berlijn, Bern, Rome</option>
                      </select>
                    </label>
                    <label className="block text-sm font-semibold text-slate-600 dark:text-slate-300">
                      {t("profile.date_format", { defaultValue: "Datumnotatie" })}
                      <select
                        value="dd MMM yyyy"
                        onChange={() => undefined}
                        className="mt-2 min-h-[44px] w-full rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-900 shadow-sm dark:border-slate-700 dark:bg-slate-950/40 dark:text-slate-100"
                      >
                        <option value="dd MMM yyyy">{currentDateExample} (dd MMM yyyy)</option>
                      </select>
                    </label>
                  </div>
                  {saved && (
                    <p className="mt-4 rounded-2xl bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800 ring-1 ring-emerald-100 dark:bg-emerald-950/30 dark:text-emerald-100 dark:ring-emerald-900/50">
                      {t("profile.saved_preferences", "Preferences saved.")}
                    </p>
                  )}
                  {error && (
                    <p className="mt-4 rounded-2xl bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700 ring-1 ring-rose-100 dark:bg-rose-950/30 dark:text-rose-100 dark:ring-rose-900/50">
                      {error}
                    </p>
                  )}
                  <div className="mt-5 flex justify-end">
                    <button type="button" onClick={saveLocale} disabled={saving || !profile} className={primaryButtonClass}>
                      {saving
                        ? t("common.saving", { defaultValue: "Saving…" })
                        : t("profile.save_changes", { defaultValue: "Save changes" })}
                    </button>
                  </div>
                </section>
              )}

              {activeProfileSection === "security" && (
                <section id="security" className={cardClasses}>
                  <div className="mb-5 flex items-center gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-200">
                      <FaShieldAlt />
                    </div>
                    <h3 className="text-lg font-bold text-slate-950 dark:text-white">
                      {t("profile.nav.security", { defaultValue: "Beveiliging" })}
                    </h3>
                  </div>
                  <div className="divide-y divide-slate-200 dark:divide-white/10">
                    <div className="flex items-center justify-between gap-4 py-4 first:pt-0">
                      <div>
                        <div className="text-sm font-bold text-slate-950 dark:text-white">
                          {t("profile.password.label", { defaultValue: "Wachtwoord" })}
                        </div>
                        <p className="text-sm text-slate-500 dark:text-slate-300">
                          {t("profile.password.last_changed_unknown", { defaultValue: "Laatst gewijzigd: onbekend" })}
                        </p>
                      </div>
                      <button type="button" onClick={openPasswordModal} className={secondaryButtonClass}>
                        {t("profile.password.open_modal", "Change password")}
                        <FaChevronRight className="ml-3" />
                      </button>
                    </div>
                    <div className="flex items-center justify-between gap-4 py-4">
                      <div>
                        <div className="text-sm font-bold text-slate-950 dark:text-white">
                          {t("profile.sessions.active", { defaultValue: "Actieve sessies" })}
                        </div>
                        <p className="text-sm text-slate-500 dark:text-slate-300">
                          {t("profile.sessions.hint", { defaultValue: "Beheer ingelogde apparaten en sessies." })}
                        </p>
                      </div>
                      <button type="button" className={secondaryButtonClass}>
                        {t("profile.sessions.view", { defaultValue: "Sessies bekijken" })}
                        <FaChevronRight className="ml-3" />
                      </button>
                    </div>
                    <div className="flex items-center justify-between gap-4 py-4 last:pb-0">
                      <div>
                        <div className="text-sm font-bold text-slate-950 dark:text-white">
                          {t("profile.mfa.title", { defaultValue: "Tweestapsverificatie (2FA)" })}
                        </div>
                        <p className="text-sm text-slate-500 dark:text-slate-300">
                          {t("profile.mfa.hint", { defaultValue: "Bescherm je account met 2FA." })}
                        </p>
                      </div>
                      <span className="inline-flex items-center rounded-full bg-slate-100 px-4 py-2 text-sm font-bold text-slate-500 dark:bg-slate-800 dark:text-slate-300">
                        {t("common.disabled", { defaultValue: "Uitgeschakeld" })}
                      </span>
                    </div>
                  </div>
                </section>
              )}

              {activeProfileSection === "notifications" && (
              <section id="notifications" className={cardClasses}>
                <div className="mb-4 flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-200">
                    <FaBell />
                  </div>
                  <h3 className="text-lg font-bold text-slate-950 dark:text-white">
                    {t("profile.nav.notifications", { defaultValue: "Notificaties" })}
                  </h3>
                </div>
                <NotificationPreferencesSection />
              </section>
              )}

              {activeProfileSection === "issues" && (
              <section id="my-issues" className={cardClasses}>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-200">
                      <FaTicketAlt />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-slate-950 dark:text-white">
                        {t("profile.my_issues.title", { defaultValue: "Mijn issues" })}
                      </h3>
                      <p className="text-sm text-slate-500 dark:text-slate-300">
                        {t("profile.my_issues.hint", {
                          defaultValue: "Issues linked to your account.",
                        })}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <label className="flex items-center gap-2 text-sm font-semibold text-slate-600 dark:text-slate-300">
                      <span>{t("common.status", { defaultValue: "Status" })}</span>
                      <select
                        value={myIssuesStatusFilter}
                        onChange={(e) => setMyIssuesStatusFilter(e.target.value)}
                        className="min-h-[44px] rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-900 shadow-sm dark:border-slate-700 dark:bg-slate-950/40 dark:text-slate-100"
                      >
                        <option value="open">Open</option>
                        <option value="closed">Closed</option>
                        <option value="all">{t("common.all", { defaultValue: "All" })}</option>
                      </select>
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        setMyIssuesError(null);
                        setMyIssuesLoaded(false);
                        setMyIssuesLoading(true);
                        http
                          .get("/issues/mine")
                          .then(({ data }) => {
                            const items = Array.isArray(data)
                              ? data
                              : Array.isArray(data?.items)
                                ? data.items
                                : Array.isArray(data?.content)
                                  ? data.content
                                  : [];
                            setMyIssues(items);
                          })
                          .catch((e) => {
                            setMyIssues([]);
                            setMyIssuesError(mapHttpErrorToMessage(e, t) || e);
                          })
                          .finally(() => {
                            setMyIssuesLoading(false);
                            setMyIssuesLoaded(true);
                          });
                      }}
                      disabled={myIssuesLoading}
                      className={secondaryButtonClass}
                    >
                      <FaSyncAlt />
                      {myIssuesLoading ? t("common.loading", { defaultValue: "Laden…" }) : t("common.refresh", { defaultValue: "Vernieuwen" })}
                    </button>
                  </div>
                </div>

                {myIssuesError && (
                  <p className="mt-4 rounded-2xl bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700 ring-1 ring-rose-100 dark:bg-rose-950/30 dark:text-rose-100 dark:ring-rose-900/50">
                    {myIssuesError?.message || myIssuesError?.title || String(myIssuesError)}
                  </p>
                )}

                <div className="mt-5 grid items-start gap-5 grid-cols-[repeat(auto-fit,minmax(min(100%,360px),1fr))]">
                  <div className="min-w-0 overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-900/80">
                    {myIssuesLoading ? (
                      <div className="p-5 text-sm text-slate-600 dark:text-slate-300">
                        {t("common.loading", { defaultValue: "Laden…" })}
                      </div>
                    ) : filteredMyIssues.length === 0 ? (
                      <div className="p-5 text-sm text-slate-600 dark:text-slate-300">
                        {t("profile.my_issues.empty", { defaultValue: "Geen issues gevonden." })}
                      </div>
                    ) : (
                      <div className="divide-y divide-slate-200 dark:divide-white/10">
                        {filteredMyIssues.slice(0, 10).map((it, idx) => {
                          const number = it?.number ?? it?.issueNumber ?? it?.id ?? it?.issue_id ?? null;
                          const title = it?.title ?? it?.summary ?? it?.name ?? "";
                          const updatedRaw =
                            it?.updatedAt ?? it?.updated_at ?? it?.lastUpdatedAt ?? it?.last_updated_at ?? it?.createdAt ?? it?.created_at ?? null;
                          const updatedDate = parseIssueCreatedAt(updatedRaw);
                          const updatedLabel =
                            updatedDate && !Number.isNaN(updatedDate.getTime())
                              ? updatedDate.toLocaleString(i18n.language, { dateStyle: "medium", timeStyle: "short" })
                              : "—";
                          const key = String(number ?? it?.uuid ?? idx);
                          const activeNumber =
                            activeIssue?.number ?? activeIssue?.issueNumber ?? activeIssue?.id ?? activeIssue?.issue_id ?? null;
                          const isSelected = number != null && activeNumber != null && String(number) === String(activeNumber);
                          return (
                            <button
                              key={key}
                              type="button"
                              onClick={() => openIssueDrawer(it)}
                              className={`flex w-full items-center gap-4 px-5 py-4 text-left transition ${
                                isSelected
                                  ? "border-l-4 border-indigo-600 bg-indigo-50/80 dark:bg-indigo-950/35"
                                  : "hover:bg-slate-50 dark:hover:bg-white/[0.04]"
                              }`}
                            >
                              <div className="w-14 shrink-0 text-sm font-black text-indigo-700 dark:text-indigo-200">
                                {number != null ? `#${number}` : "#-"}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="truncate text-sm font-bold text-slate-950 dark:text-white">{title || "—"}</div>
                                <div className="mt-1 text-xs font-semibold text-slate-500 dark:text-slate-300">{updatedLabel}</div>
                              </div>
                              <FaChevronRight className="shrink-0 text-slate-400" />
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {filteredMyIssues.length > 10 && (
                      <div className="flex items-center justify-between border-t border-slate-200 px-5 py-4 text-sm text-slate-500 dark:border-white/10 dark:text-slate-300">
                        <span>1-10 van {filteredMyIssues.length} items</span>
                        <button type="button" onClick={() => navigate("/gitea")} className="font-bold text-indigo-700 dark:text-indigo-200">
                          {t("profile.my_issues.view_all", { defaultValue: "Bekijk alle issues" })}
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="min-w-0 break-words rounded-[24px] border border-slate-200 bg-white p-4 sm:p-6 shadow-sm dark:border-white/10 dark:bg-slate-900/80">
                    {issueDrawerOpen && activeIssue ? (
                      (() => {
                        const number = issueDetails?.number ?? activeIssue?.number ?? activeIssue?.issueNumber ?? activeIssue?.id ?? "-";
                        const title = issueDetails?.title ?? activeIssue?.title ?? activeIssue?.summary ?? "";
                        const rawBody =
                          issueDetails?.body ??
                          issueDetails?.description ??
                          activeIssue?.body ??
                          activeIssue?.description ??
                          "";
                        const parsed = parseIssueBody(rawBody);
                        const created = parseIssueCreatedAt(
                          issueDetails?.createdAt ?? issueDetails?.created_at ?? activeIssue?.createdAt ?? activeIssue?.created_at ?? null
                        );
                        const updated = parseIssueCreatedAt(
                          issueDetails?.updatedAt ?? issueDetails?.updated_at ?? activeIssue?.updatedAt ?? activeIssue?.updated_at ?? null
                        );
                        const createdLabel =
                          created && !Number.isNaN(created.getTime())
                            ? created.toLocaleString(i18n.language, { dateStyle: "medium", timeStyle: "short" })
                            : "—";
                        const updatedLabel =
                          updated && !Number.isNaN(updated.getTime())
                            ? updated.toLocaleString(i18n.language, { dateStyle: "medium", timeStyle: "short" })
                            : createdLabel;
                        const reporter = issueDetails?.reporter ?? activeIssue?.reporter ?? null;
                        const reporterName = formatReporter(reporter);
                        return (
                          <div className="space-y-6">
                            <div className="flex flex-wrap items-start justify-between gap-4">
                              <div className="min-w-0 flex-1">
                                <div className="text-base font-black text-indigo-700 dark:text-indigo-200">
                                  {number !== "-" ? `#${number}` : "#-"}
                                </div>
                                <h4 className="mt-2 text-2xl font-black text-slate-950 dark:text-white">{title || "—"}</h4>
                              </div>
                            </div>

                            {issueDetailsError && (
                              <p className="rounded-2xl bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700 ring-1 ring-rose-100 dark:bg-rose-950/30 dark:text-rose-100 dark:ring-rose-900/50">
                                {issueDetailsError?.message || issueDetailsError?.title || String(issueDetailsError)}
                              </p>
                            )}

                            <div className="grid gap-4 border-b border-slate-200 pb-6 grid-cols-[repeat(auto-fit,minmax(min(100%,140px),1fr))] dark:border-white/10">
                              <div>
                                <div className="text-xs font-bold text-slate-500 dark:text-slate-300">Status</div>
                                <div className="mt-2 inline-flex items-center gap-2 rounded-full bg-sky-100 px-3 py-1 text-sm font-bold text-slate-700 dark:bg-sky-900/30 dark:text-sky-100">
                                  <span className="h-2 w-2 rounded-full bg-sky-500" />
                                  {String(issueDetails?.state ?? activeIssue?.state ?? activeIssue?.status ?? "Open")}
                                </div>
                              </div>
                              <div>
                                <div className="text-xs font-bold text-slate-500 dark:text-slate-300">Aangemaakt</div>
                                <div className="mt-2 text-sm font-semibold text-slate-900 dark:text-slate-100">{createdLabel}</div>
                              </div>
                              <div>
                                <div className="text-xs font-bold text-slate-500 dark:text-slate-300">Laatst bijgewerkt</div>
                                <div className="mt-2 text-sm font-semibold text-slate-900 dark:text-slate-100">{updatedLabel}</div>
                              </div>
                            </div>

                            <div>
                              <div className="text-sm font-bold text-slate-950 dark:text-white">Beschrijving</div>
                              <div className="mt-3 whitespace-pre-wrap text-sm leading-6 text-slate-700 dark:text-slate-200">
                                {issueDetailsLoading
                                  ? t("common.loading", { defaultValue: "Laden…" })
                                  : parsed.summary || parsed.details || "—"}
                              </div>
                            </div>

                            <div className="grid gap-5 border-y border-slate-200 py-6 grid-cols-[repeat(auto-fit,minmax(min(100%,140px),1fr))] dark:border-white/10">
                              <div>
                                <div className="text-xs font-bold text-slate-500 dark:text-slate-300">Project</div>
                                <div className="mt-2 text-sm font-semibold text-slate-900 dark:text-slate-100">Lumen Platform</div>
                              </div>
                              <div>
                                <div className="text-xs font-bold text-slate-500 dark:text-slate-300">Reporter</div>
                                <div className="mt-2 text-sm font-semibold text-slate-900 dark:text-slate-100">{reporterName}</div>
                              </div>
                            </div>

                            <div>
                              <div className="text-sm font-bold text-slate-950 dark:text-white">Activiteit</div>
                              <div className="mt-3 rounded-2xl border border-slate-200 p-4 dark:border-white/10">
                                {issueCommentsLoading ? (
                                  <p className="text-sm text-slate-600 dark:text-slate-300">{t("common.loading", { defaultValue: "Laden…" })}</p>
                                ) : sortedIssueComments.length === 0 ? (
                                  <p className="text-sm text-slate-600 dark:text-slate-300">{t("profile.my_issues.no_comments", { defaultValue: "Geen comments gevonden." })}</p>
                                ) : (
                                  <ul className="space-y-3">
                                    {sortedIssueComments.slice(0, 4).map((c, idx) => {
                                      const author = c?.authorName || c?.author || c?.user || c?.username || c?.createdBy || "";
                                      const body = c?.body || c?.text || c?.comment || c?.message || "";
                                      const key = String(c?.id ?? c?._id ?? `${idx}`);
                                      return (
                                        <li key={key} className="text-sm">
                                          <div className="font-bold text-slate-900 dark:text-slate-100">
                                            {author || t("common.unknown", { defaultValue: "Onbekend" })}
                                          </div>
                                          <div className="mt-1 whitespace-pre-wrap text-slate-700 dark:text-slate-200">{body || "—"}</div>
                                        </li>
                                      );
                                    })}
                                  </ul>
                                )}
                              </div>
                            </div>

                            <div className="flex flex-col gap-3 sm:flex-row">
                              <textarea
                                value={newIssueComment}
                                onChange={(e) => setNewIssueComment(e.target.value)}
                                rows={1}
                                className="min-w-0 min-h-[44px] flex-1 resize-y rounded-xl border border-gray-200 bg-white px-3 py-3 text-sm text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/60 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                                placeholder={t("profile.my_issues.add_comment_placeholder", { defaultValue: "Write a comment…" })}
                              />
                              <button
                                type="button"
                                onClick={submitIssueComment}
                                disabled={newIssueCommentSaving || !String(newIssueComment || "").trim() || !activeIssue}
                                className="rounded-full bg-indigo-600 px-5 text-sm font-bold text-white shadow-sm transition hover:bg-indigo-700 disabled:opacity-60"
                              >
                                {t("profile.my_issues.add_comment_submit", { defaultValue: "Post comment" })}
                              </button>
                            </div>
                          </div>
                        );
                      })()
                    ) : (
                      <div className="flex min-h-[420px] items-center justify-center rounded-2xl border border-dashed border-slate-200 text-center text-sm font-semibold text-slate-500 dark:border-white/10 dark:text-slate-300">
                        {t("profile.my_issues.select_issue", { defaultValue: "Selecteer een issue om de details te bekijken." })}
                      </div>
                    )}
                  </div>
                </div>
              </section>
              )}
            </div>
          </div>
        ) : (
          <>
            <div className={cardClasses}>
              <h2 className="text-xl font-semibold mb-4">{t("profile.title")}</h2>
              <div className="mb-4">
                <label className="block text-gray-700 dark:text-gray-300">{t("profile.username")}</label>
                <div className="bg-gray-100 dark:bg-gray-700 p-2 rounded">{username}</div>
              </div>
              <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 dark:text-gray-300">{t("profile.first_name", "First name")}</label>
                  <div className="bg-gray-100 dark:bg-gray-700 p-2 rounded">{profileFirstName || "N/A"}</div>
                </div>
                <div>
                  <label className="block text-gray-700 dark:text-gray-300">{t("profile.last_name", "Last name")}</label>
                  <div className="bg-gray-100 dark:bg-gray-700 p-2 rounded">{profileLastName || "N/A"}</div>
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 dark:text-gray-300">{t("profile.email")}</label>
                <div className="bg-gray-100 dark:bg-gray-700 p-2 rounded">{profileEmail || "N/A"}</div>
              </div>
              <div className="mb-2">
                <label className="block text-gray-700 dark:text-gray-300">{t("profile.language")}</label>
                <select
                  value={locale}
                  onChange={(e) => setLocale(e.target.value)}
                  className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                >
                  {AVAILABLE_LOCALES.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              {canChooseStartPage && (
                <div className="mt-4">
                  <StartPagePreferenceField t={t} value={effectiveStartPage} options={startPageOptions} onChange={setStartPage} />
                </div>
              )}
              {saved && <p className="mt-2 text-sm text-green-700 dark:text-green-300">{t("profile.saved_preferences", "Preferences saved.")}</p>}
              {error && <p className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p>}
              <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <button type="button" onClick={openPasswordModal} className={secondaryButtonClass}>
                  {t("profile.password.open_modal", "Change password")}
                </button>
                <button onClick={saveLocale} disabled={saving} className={primaryButtonClass}>
                  {saving ? t("common.saving", "Saving…") : t("common.save", "Save")}
                </button>
              </div>
            </div>
            {variant === "parent" && <NotificationPreferencesSection topicsConfig={parentTopicsConfig} />}
          </>
        )}
      </div>

      <Modal
        show={passwordModalOpen}
        onClose={closePasswordModal}
        title={t("profile.password.title", "Change password")}
        type="password"
        hideDefaultActions
      >
        <div className="text-left space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {t(
              "profile.password.subtitle",
              "Update your password. Make sure it is unique and not shared elsewhere."
            )}
          </p>
          <div>
            <label className="block text-gray-700 dark:text-gray-300">
              {t("profile.password.current", "Current password")}
            </label>
            <input
              type="password"
              value={passwordForm.current}
              autoComplete="current-password"
              onChange={(e) => handlePasswordInput("current", e.target.value)}
              className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
            />
          </div>
          <div>
            <label className="block text-gray-700 dark:text-gray-300">
              {t("profile.password.new", "New password")}
            </label>
            <input
              type="password"
              value={passwordForm.next}
              autoComplete="new-password"
              onChange={(e) => handlePasswordInput("next", e.target.value)}
              className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
            />
          </div>
          <div>
            <label className="block text-gray-700 dark:text-gray-300">
              {t("profile.password.confirm", "Confirm new password")}
            </label>
            <input
              type="password"
              value={passwordForm.confirm}
              autoComplete="new-password"
              onChange={(e) => handlePasswordInput("confirm", e.target.value)}
              className="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
            />
          </div>
          {passwordError && (
            <p className="text-sm text-red-600 dark:text-red-400">{passwordError}</p>
          )}
          {passwordSaved && (
            <p className="text-sm text-green-600 dark:text-green-400">
              {t("profile.password.success", "Password updated successfully.")}
            </p>
          )}
          <div className="pt-2 flex flex-col gap-3 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={closePasswordModal}
              disabled={passwordSaving}
              className="px-4 py-2 rounded border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-60"
            >
              {t("common.cancel", "Cancel")}
            </button>
            <button
              type="button"
              onClick={savePassword}
              disabled={passwordSaving}
              className={primaryButtonClass}
            >
              {passwordSaving
                ? t("profile.password.saving", "Updating…")
                : t("profile.password.save", "Update password")}
            </button>
          </div>
        </div>
      </Modal>

      {/* Issue detail modal */}
      {issueDrawerOpen && activeIssue && variant !== "staff" && (
        <div className="fixed inset-0 z-[9999] overflow-y-auto p-4 sm:p-6">
          <button
            type="button"
            className="absolute inset-0 bg-slate-950/40 backdrop-blur-[1px]"
            onClick={closeIssueDrawer}
            aria-label={t("common.close", { defaultValue: "Close" })}
          />
          <section className="lds-overview-card-enter relative mx-auto my-6 w-full max-w-6xl rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_28px_80px_rgba(15,23,42,0.24)] dark:border-white/10 dark:bg-slate-950 sm:p-7">
            <div className="flex items-start justify-between gap-4 rounded-[24px] border border-indigo-100 bg-gradient-to-r from-indigo-50 via-white to-sky-50 p-5 dark:border-white/10 dark:from-indigo-950/35 dark:via-slate-950 dark:to-sky-950/30">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Issue
                </div>
                <h3 className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {(() => {
                    const number =
                      issueDetails?.number ?? activeIssue?.number ?? activeIssue?.issueNumber ?? activeIssue?.id ?? "-";
                    const title = issueDetails?.title ?? activeIssue?.title ?? "";
                    return `${number !== "-" ? `#${number}` : "#-"}${title ? ` ${title}` : ""}`;
                  })()}
                </h3>

                {issueDetails?.workflowStatus && (
                  <div className="mt-2">
                    <span
                      className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${workflowChipClass(
                        issueDetails.workflowStatus
                      )}`}
                    >
                      {workflowStatusLabel(issueDetails.workflowStatus)}
                    </span>
                  </div>
                )}

                {issueDetailsError && (
                  <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                    {issueDetailsError?.message || issueDetailsError?.title || String(issueDetailsError)}
                  </p>
                )}

                <div className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                  {t("common.reporter", { defaultValue: "Reporter" })}:{" "}
                  {(() => {
                    const reporter = issueDetails?.reporter ?? activeIssue?.reporter ?? null;
                    const rawBody =
                      issueDetails?.body ??
                      issueDetails?.description ??
                      activeIssue?.body ??
                      activeIssue?.description ??
                      "";
                    const parsed = parseIssueBody(rawBody);
                    const reporterEmail = formatReporterEmail(reporter) || getMetaValue(parsed.meta, "Email");
                    const reporterName = formatReporter(reporter);
                    return reporterEmail ? `${reporterName} (${reporterEmail})` : reporterName;
                  })()}
                </div>
              </div>

              <button
                type="button"
                className="rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:hover:bg-gray-800"
                onClick={closeIssueDrawer}
              >
                {t("common.close", { defaultValue: "Close" })}
              </button>
            </div>

            <div className="mt-6 grid gap-6 xl:grid-cols-[minmax(0,1.25fr)_minmax(360px,0.75fr)]">
              <div>
              {(() => {
                const rawBody =
                  issueDetails?.body ??
                  issueDetails?.description ??
                  activeIssue?.body ??
                  activeIssue?.description ??
                  "";
                const parsed = parseIssueBody(rawBody);
                const bodyImages = extractImageUrls(rawBody);
                const images =
                  issueImageAssets.length > 0
                    ? issueImageAssets
                    : bodyImages.map((u) => ({ resolvedUrl: u, name: "" }));
                const page = getMetaValue(parsed.meta, "Page");
                const userAgent = getMetaValue(parsed.meta, "User-Agent");
                const roles = getMetaValue(parsed.meta, "Roles");
                const when = getMetaValue(parsed.meta, "When");
                const advancedKeys = new Set(["email", "page", "user-agent", "roles", "when"]);
                const remainingMeta = parsed.meta.filter((row) => {
                  const k = String(row?.key || "").trim().toLowerCase();
                  return k && !advancedKeys.has(k);
                });

                return (
                  <>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        {t("common.description", { defaultValue: "Omschrijving" })}
                      </div>
                      {page ? (
                        <a
                          href={page}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs font-semibold text-indigo-700 hover:underline dark:text-indigo-300"
                        >
                          Page: {page}
                        </a>
                      ) : null}
                    </div>

                    <div className="mt-2 rounded-2xl border border-gray-200 bg-white/80 p-4 text-sm text-gray-900 shadow-sm dark:border-gray-700 dark:bg-gray-900/60 dark:text-gray-100">
                      {issueDetailsLoading ? (
                        <div className="text-gray-600 dark:text-gray-300">
                          {t("common.loading", { defaultValue: "Laden…" })}
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {parsed.summary ? (
                            <div className="whitespace-pre-wrap text-sm">{parsed.summary}</div>
                          ) : (
                            <div className="text-gray-500 dark:text-gray-400">—</div>
                          )}
                          {parsed.details ? (
                            <div className="whitespace-pre-wrap text-xs text-gray-700 dark:text-gray-200">
                              {parsed.details}
                            </div>
                          ) : null}
                        </div>
                      )}
                    </div>

                    {(issueAssetsLoading || issueAssetsError || images.length > 0 || issueOtherAssets.length > 0) && (
                      <div className="mt-4">
                        <details className="rounded-2xl border border-gray-200 bg-white/80 p-4 text-xs text-gray-900 shadow-sm dark:border-gray-700 dark:bg-gray-900/60 dark:text-gray-100">
                          <summary className="cursor-pointer select-none font-semibold text-gray-700 dark:text-gray-200">
                            <span className="inline-flex items-center gap-2">
                              <FaPaperclip />
                              {t("common.attachments", { defaultValue: "Bijlagen" })}
                              <span className="text-gray-500 dark:text-gray-400">
                                ({(issueImageAssets?.length || 0) + (issueOtherAssets?.length || 0)})
                              </span>
                            </span>
                          </summary>

                          {issueAssetsLoading ? (
                            <div className="mt-3 text-sm text-gray-600 dark:text-gray-300">
                              {t("common.loading", { defaultValue: "Laden…" })}
                            </div>
                          ) : issueAssetsError ? (
                            <div className="mt-3 text-sm text-red-600 dark:text-red-400">
                              {issueAssetsError?.message || issueAssetsError?.title || String(issueAssetsError)}
                            </div>
                          ) : null}

                          {images.length > 0 && (
                            <div className="mt-3">
                              <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                {t("common.images", { defaultValue: "Afbeeldingen" })}
                              </div>
                              <div className="mt-2 grid grid-cols-3 gap-2 sm:grid-cols-4">
                                {images.map((img, index) => {
                                  const src = typeof img === "string" ? img : img?.resolvedUrl;
                                  const name = typeof img === "string" ? "" : img?.name || "";
                                  const objectUrl = issueAssetObjectUrls?.[src] || "";
                                  const baseURL = http?.defaults?.baseURL || "";
                                  const protectedUrl = isProtectedAssetUrl(src, baseURL);
                                  const canDirect =
                                    !protectedUrl &&
                                    (String(src).startsWith("data:") || /^https?:\/\//i.test(String(src)));
                                  const directSrc = objectUrl || (canDirect ? src : "");
                                  const href = objectUrl || (canDirect ? src : "#");
                                  const linkHref = href === "#" ? undefined : href;
                                  return (
                                  <a
                                    key={`${index}-${src.slice(0, 20)}`}
                                    href={linkHref}
                                    target={linkHref ? "_blank" : undefined}
                                    rel={linkHref ? "noreferrer" : undefined}
                                    className="group block overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition hover:shadow-md dark:border-gray-700 dark:bg-gray-900"
                                    title={name || undefined}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (linkHref && canDirect) return;
                                      e.preventDefault();
                                      openIssueAsset(src);
                                    }}
                                  >
                                    <img
                                      src={directSrc}
                                      alt={t("common.image", { defaultValue: "Afbeelding" })}
                                      className="h-16 w-full object-cover"
                                      loading="lazy"
                                      onError={(e) => {
                                        try {
                                          e.currentTarget.style.display = "none";
                                        } catch {
                                          // ignore
                                        }
                                      }}
                                    />
                                    <div className="hidden h-16 w-full items-center justify-center bg-slate-50 text-[11px] font-semibold text-slate-500 dark:bg-slate-900 dark:text-slate-300 group-has-[img:hidden]:flex">
                                      {t("common.image_unavailable", { defaultValue: "Niet beschikbaar" })}
                                    </div>
                                    {name ? (
                                      <div className="border-t border-gray-100 px-2 py-1 text-[10px] text-gray-600 dark:border-gray-800 dark:text-gray-300">
                                        <span className="block truncate">{name}</span>
                                      </div>
                                    ) : null}
                                  </a>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {issueOtherAssets.length > 0 && (
                            <div className="mt-4">
                              <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                {t("common.files", { defaultValue: "Bestanden" })}
                              </div>
                              <ul className="mt-2 space-y-2 text-sm">
                                {issueOtherAssets.map((a, idx) => (
                                  <li
                                    key={`${idx}-${a.resolvedUrl}`}
                                    className="flex items-center justify-between gap-3"
                                  >
                                    <span className="flex min-w-0 items-center gap-2 text-gray-700 dark:text-gray-200">
                                      <FaPaperclip className="shrink-0 opacity-70" />
                                      <span className="truncate">{a.name || a.resolvedUrl}</span>
                                    </span>
                                    <button
                                      type="button"
                                      className="shrink-0 text-xs font-semibold text-indigo-700 hover:underline dark:text-indigo-300"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        openIssueAsset(a.resolvedUrl);
                                      }}
                                    >
                                      {t("common.open", { defaultValue: "Open" })}
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </details>
                      </div>
                    )}

                    {(userAgent || roles || when || remainingMeta.length > 0) && (
                      <div className="mt-4">
                        <details className="rounded-2xl border border-gray-200 bg-white/80 p-4 text-xs text-gray-900 shadow-sm dark:border-gray-700 dark:bg-gray-900/60 dark:text-gray-100">
                          <summary className="cursor-pointer select-none font-semibold text-gray-700 dark:text-gray-200">
                            Meta-data
                          </summary>
                          <dl className="mt-3 space-y-2">
                            {when && (
                              <div className="grid grid-cols-[6rem,1fr] gap-2 py-1 border-b border-gray-100 last:border-b-0 dark:border-gray-800">
                                <dt className="font-semibold text-gray-700 dark:text-gray-200">When</dt>
                                <dd className="text-gray-700 dark:text-gray-200 break-words">{when}</dd>
                              </div>
                            )}
                            {userAgent && (
                              <div className="grid grid-cols-[6rem,1fr] gap-2 py-1 border-b border-gray-100 last:border-b-0 dark:border-gray-800">
                                <dt className="font-semibold text-gray-700 dark:text-gray-200">User-Agent</dt>
                                <dd className="text-gray-700 dark:text-gray-200 break-words">{userAgent}</dd>
                              </div>
                            )}
                            {roles && (
                              <div className="grid grid-cols-[6rem,1fr] gap-2 py-1 border-b border-gray-100 last:border-b-0 dark:border-gray-800">
                                <dt className="font-semibold text-gray-700 dark:text-gray-200">Roles</dt>
                                <dd className="text-gray-700 dark:text-gray-200 break-words">
                                  {(() => {
                                    const defaults = new Set([
                                      "offline_access",
                                      "uma_authorization",
                                      "default-roles-werfklas",
                                      "reader",
                                      "manage-account",
                                      "manage-account-links",
                                      "view-profile",
                                    ]);
                                    const all = roles.split(",").map((r) => r.trim()).filter(Boolean);
                                    const important = all.filter((r) => !defaults.has(r));
                                    const hidden = all.filter((r) => defaults.has(r));
                                    const shown = issueDrawerShowDefaultRoles ? all : important;
                                    return (
                                      <div className="space-y-2">
                                        <div className="whitespace-pre-wrap">
                                          {shown.length > 0 ? shown.join(", ") : "—"}
                                        </div>
                                        {hidden.length > 0 && (
                                          <button
                                            type="button"
                                            className="text-[11px] font-semibold text-indigo-700 underline hover:text-indigo-900 dark:text-indigo-200"
                                            onClick={() => setIssueDrawerShowDefaultRoles((v) => !v)}
                                          >
                                            {issueDrawerShowDefaultRoles
                                              ? t("common.less", { defaultValue: "Minder" })
                                              : t("common.more", { defaultValue: "Meer" })}
                                          </button>
                                        )}
                                      </div>
                                    );
                                  })()}
                                </dd>
                              </div>
                            )}
                            {remainingMeta.map((row) => (
                              <div
                                key={row.key}
                                className="grid grid-cols-[6rem,1fr] gap-2 py-1 border-b border-gray-100 last:border-b-0 dark:border-gray-800"
                              >
                                <dt className="font-semibold text-gray-700 dark:text-gray-200">{row.key}</dt>
                                <dd className="text-gray-700 dark:text-gray-200 break-words">
                                  {String(row.value || "—")}
                                </dd>
                              </div>
                            ))}
                          </dl>
                        </details>
                      </div>
                    )}
                  </>
                );
              })()}
              </div>

              <div className="space-y-6">
                <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900/80">
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      {t("profile.my_issues.comments", { defaultValue: "Comments" })}
                    </div>
                    <button
                      type="button"
                      onClick={() => openIssueDrawer(activeIssue)}
                      disabled={issueCommentsLoading || issueDetailsLoading || issueAssetsLoading}
                      className="rounded-full border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 shadow-sm hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:hover:bg-gray-800 disabled:opacity-60"
                    >
                      {issueCommentsLoading
                        ? t("common.loading", { defaultValue: "Laden…" })
                        : t("common.refresh", { defaultValue: "Vernieuwen" })}
                    </button>
                  </div>

                  {issueCommentsError && (
                    <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                      {issueCommentsError?.message || issueCommentsError?.title || String(issueCommentsError)}
                    </p>
                  )}

                  {issueCommentsLoading ? (
                    <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">
                      {t("common.loading", { defaultValue: "Laden…" })}
                    </p>
                  ) : sortedIssueComments.length === 0 ? (
                    <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">
                      {t("profile.my_issues.no_comments", { defaultValue: "Geen comments gevonden." })}
                    </p>
                  ) : (
                    <ul className="mt-4 max-h-[420px] space-y-2 overflow-y-auto pr-1">
                      {sortedIssueComments.map((c, idx) => {
                        const author =
                          c?.authorName || c?.author || c?.user || c?.username || c?.createdBy || "";
                        const body = c?.body || c?.text || c?.comment || c?.message || "";
                        const created = parseIssueCreatedAt(c?.createdAt ?? c?.created_at ?? c?.created ?? c?.timestamp ?? null);
                        const createdLabel =
                          created && !Number.isNaN(created.getTime())
                            ? created.toLocaleString(i18n.language, { dateStyle: "medium", timeStyle: "short" })
                            : "";
                        const key = String(c?.id ?? c?._id ?? `${idx}`);
                        return (
                          <li
                            key={key}
                            className="rounded-xl border border-gray-200 bg-white/70 p-3 text-sm dark:border-gray-700 dark:bg-gray-900/40"
                          >
                            <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500 dark:text-gray-400">
                              <span className="font-semibold text-gray-800 dark:text-gray-200">
                                {author || t("common.unknown", { defaultValue: "Onbekend" })}
                              </span>
                              {createdLabel && <span>{createdLabel}</span>}
                            </div>
                            <div className="mt-2 whitespace-pre-wrap text-sm text-gray-800 dark:text-gray-100">
                              {body || "—"}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>

                <div className="rounded-[24px] border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900/80">
                  <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    {t("profile.my_issues.add_comment", { defaultValue: "Add comment" })}
                  </div>
                  {newIssueCommentError && (
                    <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                      {newIssueCommentError?.message || newIssueCommentError?.title || String(newIssueCommentError)}
                    </p>
                  )}
                  <textarea
                    value={newIssueComment}
                    onChange={(e) => setNewIssueComment(e.target.value)}
                    rows={5}
                    className="mt-3 w-full resize-y rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/60 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                    placeholder={t("profile.my_issues.add_comment_placeholder", { defaultValue: "Write a comment…" })}
                  />
                  <div className="mt-3 flex justify-end">
                    <button
                      type="button"
                      onClick={submitIssueComment}
                      disabled={newIssueCommentSaving || !String(newIssueComment || "").trim() || !activeIssue}
                      className="lumen-glow-button text-sm disabled:opacity-60"
                    >
                      {newIssueCommentSaving
                        ? t("common.saving", { defaultValue: "Saving…" })
                        : t("profile.my_issues.add_comment_submit", { defaultValue: "Post comment" })}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      )}
    </>
  );
}

export default function Profile() {
  const { t } = useTranslation();
  return (
    <LdsOverviewPage>
      <LdsOverviewHero
        icon={FaUserCircle}
        eyebrow={t("navigation.profile", { defaultValue: "Account" })}
        title={t("profile.title", { defaultValue: "Profile" })}
        description={t("profile.description", {
          defaultValue: "Beheer je profielgegevens, voorkeuren en beveiliging.",
        })}
      />
      <ProfileBody variant="staff" />
    </LdsOverviewPage>
  );
}
